import React, { useState } from 'react';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext'; // Updated import path
import type { UserRole, TeamName } from '../types/core';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebaseConfig';

const LoginScreen: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
    fullName: '',
    role: 'user' as UserRole,
    team: 'Marketing A' as TeamName,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetError, setResetError] = useState('');
  const { login, registerUser } = useAuth();
  const { t } = useLanguage();
  const teams: TeamName[] = ['Marketing A', 'Marketing B', 'Marketing C', 'Marketing D', 'Marketing E', 'Production SBK', 'Production SDC'];

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const success = await login(formData.email, formData.password); // Assuming login uses email or username
      if (!success) {
        setError(t('auth.invalid', { defaultValue: 'Invalid email/username or password' }));
      }
    } catch (error: any) {
      let errorMessage = t('auth.invalid', { defaultValue: 'Invalid email/username or password' });
      if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      }
      setError(errorMessage);
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setResetError('');
    setResetSuccess('');

    if (!resetEmail) {
      setResetError(t('auth.emailRequired', { defaultValue: 'Please enter your email address' }));
      return;
    }

    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail, {
        // continueUrl users land on after completing the reset.
        // window.location.origin ensures this works on whatever domain
        // they accessed the app from (custom domain, *.web.app, localhost).
        url: `${window.location.origin}/`,
        handleCodeInApp: false,
      });
      setResetSuccess(t('auth.resetEmailSent', { defaultValue: 'Password reset email sent! Please check your inbox and spam/junk folder.' }));
      setResetEmail('');
    } catch (error: any) {
      if (error.code === 'auth/user-not-found') {
        setResetError(t('auth.userNotFound', { defaultValue: 'No account found with this email address' }));
      } else if (error.code === 'auth/invalid-email') {
        setResetError(t('auth.invalidEmail', { defaultValue: 'Please enter a valid email address' }));
      } else {
        setResetError(error.message || t('auth.resetFailed', { defaultValue: 'Failed to send reset email. Please try again.' }));
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleRegister = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');

    // Registration is now restricted to normal users only (role is fixed to 'user')
    if (!formData.email || !formData.password || !formData.username || !formData.fullName || !formData.confirmPassword) {
      setError(t('error.allFieldsRequired', { defaultValue: 'All fields are required for registration' }));
      setLoading(false);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('password.mismatch'));
      setLoading(false);
      return;
    }
    if (formData.password.length < 6) {
      setError(t('password.min.length'));
      setLoading(false);
      return;
    }

    // Remove supervisor check since only users can register

            try {
          const result = await registerUser(formData.email, formData.password, formData.username, formData.fullName, 'user', formData.team);
      if (!result.success) {
        setError(result.error || t('error.registrationFailed', { defaultValue: 'Registration failed' }));
      } else {
        // Registration successful - user is now logged in automatically
        // No need to switch to login mode or show alert since they're already authenticated
        // The AuthContext will handle the automatic login and redirect to main app
      }
    } catch (error: any) {
      setError(error.message || t('error.registrationFailed', { defaultValue: 'Registration failed' }));
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-blue-600 to-purple-700 flex items-center justify-center p-4 login-screen-container" style={{ minHeight: '100vh', width: '100vw' }}>
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md login-card">
        <div className="text-center mb-6">
          <Shield className="w-16 h-16 mx-auto text-blue-600 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">{t('app.title')}</h1>
          <p className="text-gray-600">{isLogin ? t('auth.login') : t('auth.register')}</p>
        </div>
        {!showForgotPassword && (
        <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
          <button type="button" onClick={() => { setIsLogin(true); setError(''); setFormData(prev => ({ ...prev, password: '', confirmPassword: '' })); }} className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${isLogin ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>{t('auth.login')}</button>
          <button type="button" onClick={() => { setIsLogin(false); setError(''); setFormData(prev => ({ ...prev, password: '', confirmPassword: '', role: 'user' })); }} className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${!isLogin ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>{t('auth.register')}</button>
        </div>
        )}

        {showForgotPassword ? (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                {t('auth.resetPassword', { defaultValue: 'Reset Password' })}
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                {t('auth.resetPasswordDescription', { defaultValue: "Enter your email address and we'll send you a link to reset your password." })}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">{t('auth.email')} *</label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                placeholder="your.email@company.com"
                autoComplete="email"
                disabled={resetLoading}
              />
            </div>
            {resetError && (<div className="login-error-box"><p className="login-error-text">{resetError}</p></div>)}
            {resetSuccess && (
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-green-800 text-sm">{resetSuccess}</p>
              </div>
            )}
            <button type="submit" disabled={resetLoading} className="btn-primary btn-login-submit w-full disabled:opacity-50 disabled:cursor-not-allowed">
              {resetLoading
                ? t('auth.sendingResetEmail', { defaultValue: 'Sending...' })
                : t('auth.sendResetEmail', { defaultValue: 'Send Reset Email' })}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setResetError('');
                setResetSuccess('');
                setResetEmail('');
              }}
              disabled={resetLoading}
              className="w-full text-sm text-blue-600 hover:text-blue-500 disabled:opacity-50"
            >
              {t('auth.backToSignIn', { defaultValue: 'Back to Sign In' })}
            </button>
          </form>
        ) : (
        <form onSubmit={isLogin ? handleLogin : handleRegister} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">{isLogin ? t('auth.emailOrUsername', { defaultValue: 'Email or Username' }) : t('auth.email')} *</label>
            <input type={isLogin ? "text" : "email"} value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required placeholder={isLogin ? t('auth.emailOrUsername.placeholder', { defaultValue: 'Enter email or username' }) : "your.email@company.com"} />
          </div>
          <div className="relative">
            <label className="block text-sm font-medium text-gray-600 mb-1">{t('auth.password')} *</label>
            <input 
              type={showPassword ? "text" : "password"} 
              value={formData.password} 
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))} 
              required 
              placeholder={t('auth.password.placeholder', { defaultValue: 'Enter your password' })} 
              autoComplete={isLogin ? "current-password" : "new-password"}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 pt-5 text-gray-500 hover:text-gray-700 focus:outline-none" aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
          </div>
          {!isLogin && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">{t('auth.confirmPassword')} *</label>
                <div className="relative">
                  <input 
                    type={showConfirmPassword ? "text" : "password"} 
                    value={formData.confirmPassword} 
                    onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))} 
                    required 
                    placeholder={t('auth.confirmPassword.placeholder', { defaultValue: 'Confirm your password' })} 
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 pt-5 text-gray-500 hover:text-gray-700 focus:outline-none" aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}>{showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
                </div>
              </div>
              <div><label className="block text-sm font-medium text-gray-600 mb-1">{t('auth.username')} *</label><input type="text" value={formData.username} onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))} required placeholder="username" /></div>
              <div><label className="block text-sm font-medium text-gray-600 mb-1">{t('user.fullname')} *</label><input type="text" value={formData.fullName} onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))} required placeholder="John Doe" /></div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">{t('user.role')}</label>
                <div className="p-2 border rounded bg-gray-100 text-gray-700">
                  {t('user.user')} {/* Only normal users can register through login page */}
                </div>
                <input type="hidden" value="user" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">{t('user.team')} *</label>
                <select value={formData.team} onChange={(e) => setFormData(prev => ({ ...prev, team: e.target.value as TeamName }))} required>
                  {teams.map(team => (<option key={team} value={team}>{team}</option>))}
                </select>
              </div>
            </>
          )}
          {error && (<div className="login-error-box"><p className="login-error-text">{error}</p></div>)}
          {isLogin && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(true);
                  setError('');
                  setResetError('');
                  setResetSuccess('');
                  setResetEmail(formData.email);
                }}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                {t('auth.forgotPassword', { defaultValue: 'Forgot password?' })}
              </button>
            </div>
          )}
          <button type="submit" disabled={loading} className="btn-primary btn-login-submit w-full disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? (isLogin ? t('auth.signingIn', { defaultValue: 'Signing in...' }) : t('auth.creatingAccount', { defaultValue: 'Creating account...' })) : (isLogin ? t('auth.login.button') : t('auth.register.button'))}
          </button>
        </form>
        )}
        {!showForgotPassword && isLogin && (<div className="mt-4 text-center text-sm text-gray-600"><p>{t('auth.noAccountPrompt', { defaultValue: "Don't have an account?" })} <button type="button" onClick={() => { setIsLogin(false); setError(''); setFormData(prev => ({ ...prev, password: '', confirmPassword: '', role: 'user' })); }} className="font-medium text-blue-600 hover:text-blue-500">{t('auth.register')}</button></p></div>)}
        {!showForgotPassword && !isLogin && (<div className="mt-4 text-center text-sm text-gray-600"><p>{t('auth.alreadyAccountPrompt', { defaultValue: 'Already have an account?' })} <button type="button" onClick={() => { setIsLogin(true); setError(''); setFormData(prev => ({ ...prev, password: '', confirmPassword: '' })); }} className="font-medium text-blue-600 hover:text-blue-500">{t('auth.login')}</button></p></div>)}
      </div>
    </div>
  );
};

export default LoginScreen;
